/* eslint-disable indent */
import _ from 'lodash';
import { StoryHistory } from 'client/utils';
import { CardData, StoryModel, StoryStep, CardDataWithStepInfo, StoryVersionType, StorySymbols } from 'types/story';

export type RequestMainRowData = {
	storyId: StoryModel['id'];
	name: StoryModel['name'];
	organizationId: StoryModel['organizationId'];
	type: StoryModel['type'];
	language: StoryModel['language'];
	tags: StoryModel['tags'];
	teamId?: StoryModel['teamId'];
};

const VERSIONS = {
	latest: 'latest',
	published: 'published',
	archived: 'archived',
	unpublished: 'unpublished',
} as const;

/**
 * @param name
 * @param storyId
 * @param teamId
 * @param organizationId
 * @param storyData
 * @constructor
 */
class StoryFacade {
	static VERSIONS = VERSIONS;

	base: StoryModel;

	// todo: story can be undefined. need change a type and do a big refactor
	story: StoryVersionType;

	constructor(story: StoryModel, defaultVersion: string = StoryFacade.VERSIONS.latest) {
		this.base = story;
		this.story = this.getVersion(defaultVersion);
	}

	getVersion(version: string): StoryVersionType {
		return _.get(this.base, ['storyVersions', version]);
	}

	setVersion(version: string) {
		this.story = this.getVersion(version);
		return this;
	}

	/**
	 * Provide data in the format required for an update request
	 * @return {{name: *, storyData: *, storyId: *}}
	 */
	get requestData() {
		return {
			storyId: this.storyId,
			storyData: this.data,
			settings: this.settings,
		};
	}

	get requestMainRowData(): RequestMainRowData {
		return {
			storyId: this.storyId,
			name: this.name,
			organizationId: this.base.organizationId,
			type: this.base.type,
			language: this.base.language,
			tags: this.base.tags,
			teamId: this.base.teamId || undefined,
		};
	}

	get language() {
		return this.base.language;
	}

	get settings() {
		return this.story.settings || {};
	}

	get name() {
		return this.base.name;
	}

	get isPublished() {
		return this.story.version === VERSIONS.published;
	}

	get data() {
		return this.story.data || {};
	}

	get steps(): StoryVersionType['data']['steps'] {
		return this.data.steps || [];
	}

	get elements(): StoryVersionType['data']['elements'] {
		return this.data.elements || [];
	}

	get mediaQuery(): StoryVersionType['data']['mediaQuery'] {
		return this.data.mediaQuery;
	}

	get storyId() {
		return this.base.id;
	}

	get symbols(): StorySymbols {
		return this.data.symbols || {};
	}

	get storyResults() {
		const sHistory = new StoryHistory({ storyId: this.storyId });
		return sHistory.totalResults(this.settings, this.cards);
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cards(): CardData[] {
		const { steps } = this;
		const cards = _.reduce<StoryStep, CardData[]>(
			steps,
			(result, step) => {
				return result.concat(step.cards);
			},
			[]
		);

		return cards;
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cardsWithStepInfo(): CardDataWithStepInfo[] {
		const { steps } = this;
		const cards = _.reduce<StoryStep, (CardData & { step: { _id: StoryStep['_id'] } })[]>(
			steps,
			(result, step) => {
				const cardsWithStep = _.map(step.cards, card => ({
					...card,
					step: { ..._.pick(step, ['_id']) },
				}));

				return result.concat(cardsWithStep);
			},
			[]
		);
		return cards;
	}

	get dataByFirstCard() {
		const result: { card: CardData | null; step: StoryStep | null } = { card: null, step: null };
		const { steps } = this;

		steps.some(step => {
			return (step.cards || []).some(card => {
				const isMatch = _.has(card, '_id');
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});
		return result;
	}

	canUseThirdPartyCookies() {
		const isSubdomainConnected = this.base.domainId !== null;
		const isEmbed = this.base.type === 'embed';
		return (isEmbed && isSubdomainConnected) || !isEmbed;
	}

	getDataByCardId(cardId): { card?: CardData; step?: StoryStep } {
		const { steps } = this;
		const result: { card?: CardData; step?: StoryStep } = {};

		steps.some(step => {
			return _.get(step, 'cards', []).some(card => {
				const isMatch = card._id === cardId;
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});

		return result;
	}

	getNextDefaultCard(step: StoryStep): CardData | undefined {
		const stepIndex: number = _.findIndex(this.steps, ['_id', step._id]);

		if (stepIndex < 0) {
			return undefined;
		}

		// get next step with cards
		const nextStep = _.find(_.slice(this.steps, stepIndex + 1), s => _.size(_.get(s, 'cards')));
		const nextStepCards = _.get(nextStep, 'cards');
		const nextCard = _.find(nextStepCards, card => card._id);

		return nextCard;
	}

	// get text with a replaced supported dynamic tet variables
	getTransformedDynamicText(text: string = '') {
		const results = this.storyResults;

		return text
			.replace('$score', `${Math.ceil(results.scoreRelative) ?? '0'}/100`)
			.replace('$answers', `${results.answersTotal ?? '0'}`)
			.replace('$correct', `${results.answersCorrect ?? '0'}`);
	}
}

//  pick limited versions count to not overload "redux"
function filterStoryVersions(story: StoryModel, limit = 2): StoryModel {
	const storyVersions = {};
	const latest = story.storyVersions[StoryFacade.VERSIONS.latest];
	const published = story.storyVersions[StoryFacade.VERSIONS.published];

	[StoryFacade.VERSIONS.archived, StoryFacade.VERSIONS.unpublished].forEach(version => {
		const result = Object.values(story.storyVersions)
			.filter(ver => ver.version.startsWith(version))
			.sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt))
			.slice(0, limit)
			.reduce((acc, cur) => {
				acc[cur.version] = cur;
				return acc;
			}, {});

		if (Object.keys(result)) {
			Object.assign(storyVersions, result);
		}
	});

	if (latest) {
		Object.assign(storyVersions, { latest });
	}

	if (published) {
		Object.assign(storyVersions, { published });
	}

	return { ...story, storyVersions };
}

export { StoryFacade, filterStoryVersions };
